import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { Megaphone, Plus, Trash2, RefreshCw, CheckCircle2, AlertTriangle, KeyRound } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { MetaSyncSchedule } from './MetaSyncSchedule';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const metaApi = async (path, opts = {}) => {
  const res = await fetch(`${API_URL}/api${path}`, {
    credentials: 'include', headers: { 'Content-Type': 'application/json' }, ...opts,
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(body.detail || `Request failed (${res.status})`);
  return body;
};

export const useMetaSync = (onDone) => {
  const [running, setRunning] = useState(false);
  const timer = useRef(null);
  const poll = useCallback(() => {
    timer.current = setTimeout(async () => {
      try {
        const d = await metaApi('/meta-accounts');
        if (d.sync?.running) return poll();
        setRunning(false);
        if (d.sync?.last_error) toast.error(`ROAS sync failed: ${d.sync.last_error}`);
        else toast.success(`ROAS synced · ${d.sync?.last_summary?.creatives_updated ?? 0} creatives updated`);
        onDone?.(d);
      } catch { setRunning(false); }
    }, 4000);
  }, [onDone]);
  useEffect(() => () => clearTimeout(timer.current), []);
  const start = async (creativeIds) => {
    const ids = Array.isArray(creativeIds) ? creativeIds : [];
    if (ids.length > 25) { toast.error('Select up to 25 creatives to sync, or clear the selection to sync everything'); return; }
    try {
      const r = await metaApi('/admin/sync-roas', {
        method: 'POST', body: ids.length ? JSON.stringify({ creative_ids: ids }) : undefined,
      });
      setRunning(true);
      toast.info(!r.started ? 'A sync is already running'
        : ids.length ? `Syncing ${ids.length} selected creative${ids.length > 1 ? 's' : ''} from Meta`
          : 'ROAS sync started — pulling lifetime numbers from Meta');
      poll();
    } catch (e) { toast.error(e.message); }
  };
  return { running, start };
};

const AccountRow = ({ acc, isAdmin, onToggle, onDelete }) => (
  <div className="flex items-center gap-3 py-3" data-testid={`meta-account-row-${acc.account_id}`}>
    <div className="min-w-0 flex-1">
      <div className="flex items-center gap-2">
        <span className="font-medium text-slate-900 truncate">{acc.name}</span>
        <code className="text-[11px] text-slate-500">{acc.account_id}</code>
        {acc.currency && <Badge variant="outline" className="text-[10px]">{acc.currency}</Badge>}
      </div>
      <div className="mt-0.5 text-xs text-slate-500">
        {acc.last_sync_at ? (
          acc.last_sync_status === 'error'
            ? <span className="text-red-500" title={acc.last_sync_error}>Last sync failed · {acc.last_sync_error}</span>
            : <>Synced {formatDistanceToNow(new Date(acc.last_sync_at))} ago · {acc.last_creatives_matched} creatives matched from {acc.last_rows_fetched} ads</>
        ) : 'Not synced yet'}
      </div>
    </div>
    <Switch checked={acc.enabled} disabled={!isAdmin} onCheckedChange={(v) => onToggle(acc, v)} data-testid={`meta-account-toggle-${acc.account_id}`} />
    {isAdmin && (
      <Button variant="ghost" size="icon" onClick={() => onDelete(acc)} className="text-slate-400 hover:text-red-500" data-testid={`meta-account-delete-${acc.account_id}`}>
        <Trash2 className="h-4 w-4" />
      </Button>
    )}
  </div>
);

const AddAccountForm = ({ onAdded }) => {
  const [name, setName] = useState('');
  const [accountId, setAccountId] = useState('');
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await metaApi('/meta-accounts', { method: 'POST', body: JSON.stringify({ name, account_id: accountId }) });
      setName(''); setAccountId('');
      toast.success('Ad account added');
      onAdded();
    } catch (err) { toast.error(err.message); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="flex flex-wrap gap-2 pt-3">
      <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Friendly name (e.g. Wellversed India)" className="flex-1 min-w-[200px]" data-testid="meta-account-name-input" />
      <Input value={accountId} onChange={(e) => setAccountId(e.target.value)} placeholder="act_1234567890" className="w-56 font-mono text-sm" data-testid="meta-account-id-input" />
      <Button type="submit" disabled={saving || !name.trim() || !accountId.trim()} className="bg-[#2EB4AC] hover:bg-[#25908A]" data-testid="meta-account-add-btn">
        <Plus className="h-4 w-4 mr-1" /> Add account
      </Button>
    </form>
  );
};

export const MetaAdAccountsCard = () => {
  const [data, setData] = useState(null);
  const load = useCallback(() => metaApi('/meta-accounts').then(setData).catch((e) => toast.error(e.message)), []);
  useEffect(() => { load(); }, [load]);
  const { running, start } = useMetaSync(load);

  const toggle = async (acc, enabled) => {
    try { await metaApi(`/meta-accounts/${acc.id}`, { method: 'PATCH', body: JSON.stringify({ enabled }) }); load(); }
    catch (e) { toast.error(e.message); }
  };
  const [pendingDelete, setPendingDelete] = useState(null);
  const remove = async () => {
    const acc = pendingDelete;
    setPendingDelete(null);
    try { await metaApi(`/meta-accounts/${acc.id}`, { method: 'DELETE' }); load(); }
    catch (e) { toast.error(e.message); }
  };

  const syncing = running || data?.sync?.running;
  return (
    <Card className="bg-white border border-slate-200 shadow-sm" data-testid="meta-accounts-card">
      <CardHeader>
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-12 h-12 rounded-lg bg-blue-50 flex items-center justify-center">
            <Megaphone className="h-6 w-6 text-blue-600" />
          </div>
          <div className="flex-1">
            <CardTitle className="text-lg">Meta Ad Accounts</CardTitle>
            <CardDescription>Lifetime ROAS, spend, CTR, CPA, CVR and live status synced onto the Creative Log by ad name</CardDescription>
          </div>
          {data && (data.token_configured
            ? <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200" data-testid="meta-token-status"><CheckCircle2 className="h-3 w-3 mr-1" />Token connected</Badge>
            : <Badge className="bg-amber-50 text-amber-700 border border-amber-200" data-testid="meta-token-status"><KeyRound className="h-3 w-3 mr-1" />Token missing</Badge>)}
        </div>
      </CardHeader>
      <CardContent>
        {data && !data.token_configured && (
          <div className="mb-3 flex gap-2 rounded-lg bg-amber-50 p-3 text-xs text-amber-800">
            <AlertTriangle className="h-4 w-4 shrink-0" />
            Add META_SYSTEM_USER_TOKEN (System User token with ads_read) to the app secrets. Accounts can be added now; they're verified with Meta once the token is set.
          </div>
        )}
        <div className="divide-y divide-slate-100">
          {data?.accounts?.length === 0 && <p className="py-4 text-sm text-slate-400">No ad accounts yet</p>}
          {data?.accounts?.map((a) => <AccountRow key={a.id} acc={a} isAdmin={data.is_admin} onToggle={toggle} onDelete={setPendingDelete} />)}
        </div>
        <AlertDialog open={!!pendingDelete} onOpenChange={(o) => !o && setPendingDelete(null)}>
          <AlertDialogContent data-testid="meta-account-delete-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>Remove {pendingDelete?.name}?</AlertDialogTitle>
              <AlertDialogDescription>It won't be synced anymore. Numbers already synced stay on the creatives.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="meta-account-delete-cancel">Cancel</AlertDialogCancel>
              <AlertDialogAction onClick={remove} className="bg-red-600 hover:bg-red-700" data-testid="meta-account-delete-confirm">Remove</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        {data?.is_admin && <AddAccountForm onAdded={load} />}
        {data?.is_admin && (
          <div className="mt-4 flex items-center justify-between border-t border-slate-100 pt-4">
            <span className="text-xs text-slate-500">
              {data.sync?.last_summary ? `Last run ${formatDistanceToNow(new Date(data.sync.last_summary.finished_at))} ago (${data.sync.last_summary.trigger}) · ${data.sync.last_summary.creatives_updated} creatives updated` : 'No sync has run yet'}
            </span>
            <Button variant="outline" size="sm" onClick={() => start()} disabled={syncing || !data.token_configured || !data.accounts.length} data-testid="meta-sync-now-btn">
              <RefreshCw className={`h-4 w-4 mr-1.5 ${syncing ? 'animate-spin' : ''}`} /> {syncing ? 'Syncing…' : 'Sync now'}
            </Button>
          </div>
        )}
        {data?.token_configured && <MetaSyncSchedule isAdmin={data.is_admin} />}
      </CardContent>
    </Card>
  );
};
